import {ForwardedRef, forwardRef, ReactElement, ReactNode} from "react";
import {components, OptionProps, SelectInstance} from "react-select";

import {CheckboxInput} from "@/components/checkbox/CheckboxInput/CheckboxInput.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {EllipsisText} from "@/components/typography/EllipsisText.tsx";

export interface ListPanelSelectOption<V extends string | number = number> {
  value: V;
  title: string;
  subtitle?: string;
  tag?: string;
  accessory?: ReactNode;
}

export type OptionAccessoryRenderer<V extends string | number = number> = (
  option: ListPanelSelectOption<V>
) => ReactNode;

const ListPanelOption =
  <V extends string | number>(
    hideTag?: boolean,
    optionAccessory?: OptionAccessoryRenderer<V>,
    showCheckbox?: boolean
  ) =>
  ({data, ...props}: OptionProps<ListPanelSelectOption<V>, true>) => {
    return (
      <components.Option {...props} data={data}>
        <div className="d-flex align-items-center">
          {showCheckbox && <CheckboxInput type="checkbox" checked={props.isSelected} readOnly tabIndex={-1} />}
          <div className="col overflow-hidden">
            <div className="d-flexflex-row align-items-center justify-content-between">
              <div>
                <div className="d-flex">
                  <EllipsisText toolTip={data.title} className="col mx-2">
                    {data.title}
                  </EllipsisText>
                  {!hideTag && <span className="col-2 mx-2 w-auto text-end pulse-color-gray-600">{data.tag}</span>}
                </div>
                {data.subtitle && (
                  <div className="d-flex">
                    <EllipsisText toolTip={data.subtitle} className="col mx-2 pulse-color-gray-600">
                      {data.subtitle}
                    </EllipsisText>
                  </div>
                )}
              </div>
            </div>
          </div>
          {optionAccessory && <div className="flex-shrink-0 mx-2">{optionAccessory(data)}</div>}
          {data.accessory && <div>{data.accessory}</div>}
        </div>
      </components.Option>
    );
  };

export type ListPanelSelectProps<V extends string | number = number> = SelectProps<ListPanelSelectOption<V>, true> & {
  noOptionsText?: ReactNode;
  hideTag?: boolean;
  showCheckbox?: boolean;
  optionAccessory?: OptionAccessoryRenderer<V>;
};

const ListPanelSelectInner = <V extends string | number = number>(
  {noOptionsText, hideTag, showCheckbox, optionAccessory, ...props}: ListPanelSelectProps<V>,
  ref: ForwardedRef<SelectInstance<ListPanelSelectOption<V>, true>>
) => {
  return (
    <Select
      components={{
        Control: () => null,
        Option: ListPanelOption<V>(hideTag, optionAccessory, showCheckbox),
        ...(noOptionsText !== undefined && {
          NoOptionsMessage: () => (
            <div className="pulseTextBodySmall pulse-color-gray-600 text-center">{noOptionsText}</div>
          ),
        }),
      }}
      menuIsOpen
      isMulti
      ref={ref}
      menuShouldScrollIntoView={false}
      {...props}
    />
  );
};

export const ListPanelSelect = forwardRef(ListPanelSelectInner) as <V extends string | number = number>(
  props: ListPanelSelectProps<V> & {ref?: ForwardedRef<SelectInstance<ListPanelSelectOption<V>, true>>}
) => ReactElement;
